import {
  appendQuery,
  buildCellServiceUrl,
  type CellServiceQuery,
} from './cell-session';
import { getTenantIdFromToken } from './csm-utils';
import {
  extractPathAfterBulk,
  filterMedia,
  filterSources,
  getTokenInfo,
  safeDecodeURIComponent,
} from './functions';
import { prepareGzipJsonBody } from './cors-json-body-compression';
import { isAbortError, withTokenAuthRecovery } from './cors-auth-recovery';
import { mapSlideToTimelineEventPayload } from './timeline-event-payload';

const API_PREFIX = '/timeline-service/v1';

export class TimelineApiError extends Error {
  public readonly status: number | null;
  public readonly detail: string | null;

  constructor(message: string, status: number | null = null, detail: string | null = null) {
    super(message);
    this.name = 'TimelineApiError';
    this.status = status;
    this.detail = detail;
  }
}

export function buildTimelineUrl(cellId: string, path: string): string {
  return buildCellServiceUrl(cellId, API_PREFIX, path);
}

export type TimelineQuery = CellServiceQuery;

type TimelineSessionArgs = {
  cellId: string;
  accessToken: string;
  idToken?: string | null;
};

type TimelineRequestArgs = TimelineSessionArgs & {
  path: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: TimelineQuery;
  signal?: AbortSignal;
  timeoutMs?: number;
};

export type TimelineListItem = {
  id: string;
  tags?: string[];
  title?: string;
  description?: string;
  createdBy?: string;
  createdAt?: string;
  eventCount?: number;
};

export type TimelineBundleResponse = {
  timeline?: Record<string, unknown>;
  sources: unknown[];
  media: unknown[];
  timelineList: TimelineListItem[];
  error?: string;
};

function assertSession(cellId: string, accessToken: string): void {
  if (!cellId) {
    throw new TimelineApiError('Missing cellId (custom:cell_id claim) on the ID token.');
  }
  if (!accessToken) {
    throw new TimelineApiError('Missing access token.');
  }
}

function isUuidLikeTenantId(id: string): boolean {
  const compact = id.replace(/-/g, '').replace(/\s/g, '').toLowerCase();
  return compact.length === 32 && /^[0-9a-f]+$/.test(compact);
}

function displayNameToTenantSlug(display: string): string {
  return display.trim().replace(/\s+/g, '-').toLowerCase();
}

function buildTimelineClientHeaders(args: TimelineSessionArgs): Record<string, string> {
  const idTokenInfo = (args.idToken ? getTokenInfo(args.idToken) : null) as Record<string, unknown> | null;
  const accessTokenInfo = getTokenInfo(args.accessToken) as Record<string, unknown> | null;
  const tenantId =
    getTenantIdFromToken(args.idToken || null) ||
    resolveTenantId(accessTokenInfo, idTokenInfo);
  const tenantNameRaw =
    (typeof idTokenInfo?.['custom:tenant_name'] === 'string' && idTokenInfo['custom:tenant_name']) ||
    (typeof accessTokenInfo?.['custom:tenant_name'] === 'string' && accessTokenInfo['custom:tenant_name']) ||
    '';
  const tenantName = tenantNameRaw.trim();
  const rawTenant =
    (typeof idTokenInfo?.['custom:tenant'] === 'string' && idTokenInfo['custom:tenant']) ||
    (typeof accessTokenInfo?.['custom:tenant'] === 'string' && accessTokenInfo['custom:tenant']) ||
    '';
  const compactTenant = rawTenant.replace(/-/g, '');
  const customerIdHeader =
    compactTenant.length === 32 && /^[0-9a-fA-F]{32}$/.test(compactTenant)
      ? compactTenant.toLowerCase()
      : tenantId.replace(/-/g, '').toLowerCase();
  const slugFromTokenName =
    isUuidLikeTenantId(tenantId) && tenantName ? displayNameToTenantSlug(tenantName) : '';
  const xTenantIdForResolution =
    isUuidLikeTenantId(tenantId) && slugFromTokenName ? slugFromTokenName : tenantId;

  const headers: Record<string, string> = {
    'x-tai-customer-id': customerIdHeader,
    'x-tenant-id': xTenantIdForResolution,
  };
  if (tenantName) {
    headers['x-tai-tenant-name'] = tenantName;
  }
  return headers;
}

function resolveTenantId(
  accessTokenInfo: Record<string, unknown> | null,
  idTokenInfo: Record<string, unknown> | null,
): string {
  return (
    (accessTokenInfo?.['custom:tenant'] as string | undefined) ||
    (accessTokenInfo?.['custom:tenant_id'] as string | undefined) ||
    (idTokenInfo?.['custom:tenant_id'] as string | undefined) ||
    (idTokenInfo?.['custom:tenant'] as string | undefined) ||
    ''
  );
}

async function readErrorAndThrow(res: Response, path: string): Promise<never> {
  const detail = await res.text().catch(() => '');
  throw new TimelineApiError(
    `timeline-service ${path} returned ${res.status}`,
    res.status,
    detail || null,
  );
}

let timelineWriteCount = 0;

/**
 * Count of timeline-service writes started in this page. A caller that shares
 * an in-flight read compares it before and after, because a read whose GET
 * went out before a write can return the state from before that write.
 */
export function getTimelineWriteCount(): number {
  return timelineWriteCount;
}

/** Shared URL/body compression/auth/fetch for timeline-service cell calls. */
async function timelineServiceFetch(args: TimelineRequestArgs): Promise<Response> {
  const { cellId, accessToken, idToken, path, method, body, query, signal, timeoutMs = 90_000 } = args;
  if (method !== 'GET') {
    timelineWriteCount += 1;
  }
  assertSession(cellId, accessToken);

  const url = appendQuery(buildTimelineUrl(cellId, path), query);
  const { body: outboundBody, extraHeaders } = await prepareGzipJsonBody(body);
  const hasBody = outboundBody !== undefined;

  try {
    return await fetch(url, {
      method,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
        ...buildTimelineClientHeaders({ cellId, accessToken, idToken }),
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...extraHeaders,
      },
      body: outboundBody,
      signal: signal ?? AbortSignal.timeout(timeoutMs),
    });
  } catch (error) {
    // A caller-cancelled request is not a failure. Wrapping it would match
    // isLikelyCorsOrNetworkFailure and trigger a token refresh for a cancel.
    if (isAbortError(error)) throw error;
    throw new TimelineApiError(
      `Network error calling timeline-service ${path}: ${(error as Error).message}`,
    );
  }
}

async function timelineRequest<T>(args: TimelineRequestArgs): Promise<T> {
  const { path } = args;
  return withTokenAuthRecovery(args.accessToken, async (accessToken) => {
    const res = await timelineServiceFetch({ ...args, accessToken });

    if (!res.ok) {
      await readErrorAndThrow(res, path);
    }

    if (res.status === 204) {
      return null as unknown as T;
    }

    return (await res.json().catch(() => null)) as T;
  });
}

function mapTimelineList(timelines: Array<Record<string, unknown>>): TimelineListItem[] {
  return timelines.map((timeline) => ({
    id: String(timeline.id),
    tags: timeline.tags as string[] | undefined,
    title: timeline.title as string | undefined,
    description: timeline.description as string | undefined,
    createdBy: timeline.createdBy as string | undefined,
    createdAt: timeline.createdAt as string | undefined,
    eventCount: typeof timeline.eventCount === 'number'
      ? timeline.eventCount
      : Array.isArray(timeline.events)
        ? timeline.events.length
        : 0,
  }));
}

function collectSourcesAndMedia(timeline: Record<string, unknown>): {
  sources: unknown[];
  media: unknown[];
} {
  const allSources: unknown[] = [];
  const allMedia: unknown[] = [];
  const events = Array.isArray(timeline.events) ? timeline.events : [];
  for (const event of events) {
    if (!event || typeof event !== 'object') continue;
    const eventRecord = event as Record<string, unknown>;
    const mediaItems = Array.isArray(eventRecord.media) ? eventRecord.media : [];
    for (const media of mediaItems) {
      if (!media || typeof media !== 'object') continue;
      const mediaRecord = { ...(media as Record<string, unknown>) };
      const mediaUrl = typeof mediaRecord.mediaUrl === 'string' ? mediaRecord.mediaUrl : '';
      if (mediaUrl) {
        mediaRecord.originalUrl = mediaUrl;
        mediaRecord.s3Url = mediaUrl;
        mediaRecord.presignedUrl = mediaUrl;
        mediaRecord.mediaUrl = mediaUrl;
      }
      allMedia.push(mediaRecord);
    }
    const sourceItems = Array.isArray(eventRecord.sources) ? eventRecord.sources : [];
    for (const source of sourceItems) {
      if (!source || typeof source !== 'object') continue;
      const sourceRecord = { ...(source as Record<string, unknown>) };
      const sourceUrl = typeof sourceRecord.sourceUrl === 'string' ? sourceRecord.sourceUrl : '';
      if (sourceUrl) {
        const truncatedSource = extractPathAfterBulk(sourceUrl);
        sourceRecord.truncatedFilePath = truncatedSource.truncatedFilePath;
        sourceRecord.sourceType = truncatedSource.sourceType;
        sourceRecord.originalUrl = sourceUrl;
        sourceRecord.s3Url = sourceUrl;
        sourceRecord.presignedUrl = sourceUrl;
        sourceRecord.sourceUrl = sourceUrl;
      }
      allSources.push(sourceRecord);
    }
  }
  return {
    sources: filterSources(allSources as never[]),
    media: filterMedia(allMedia as never[]),
  };
}

function normalizeTimelinesResponse(
  timelines: Array<Record<string, unknown>>,
  docId?: string | null,
): TimelineBundleResponse {
  const timelineList = mapTimelineList(timelines);
  if (!docId || docId.trim() === '') {
    return { timeline: undefined, sources: [], media: [], timelineList };
  }

  const timeline = timelines.find((item) => String(item.id) === docId);
  if (!timeline) {
    return {
      error: 'No timelines found for the given parameters',
      timeline: undefined,
      sources: [],
      media: [],
      timelineList,
    };
  }

  const { sources, media } = collectSourcesAndMedia(timeline);
  return {
    timeline,
    sources,
    media,
    timelineList,
  };
}

export type QueryTimelinesArgs = TimelineSessionArgs & {
  docId?: string | null;
  tags: string;
  signal?: AbortSignal;
};

export async function queryTimelines(args: QueryTimelinesArgs): Promise<TimelineBundleResponse> {
  const timelines = await timelineRequest<Array<Record<string, unknown>>>({
    ...args,
    path: '/timelines',
    method: 'GET',
    query: { tag: args.tags },
  });
  if (!Array.isArray(timelines)) {
    throw new TimelineApiError('timeline-service /timelines returned a non-array body.');
  }
  return normalizeTimelinesResponse(timelines, args.docId);
}

export type CreateTimelineArgs = TimelineSessionArgs & {
  title: string;
  description: string;
  caseId: string;
  /**
   * The S3 folder name for ``caseId`` — rename-safe. Tagged instead of the display
   * name so the timeline survives a rename and stays visible to the caller, whose
   * access tags are ``case:<folderName>``. Omit for a legacy case, whose folder name
   * IS its display name.
   */
  caseFolderName?: string | null;
  signal?: AbortSignal;
};

export async function createTimeline(args: CreateTimelineArgs): Promise<{ timeline: Record<string, unknown> }> {
  const tagsForCreate = `CASE:${args.caseFolderName?.trim() || args.caseId}`;
  const encodedTags = tagsForCreate ? safeDecodeURIComponent(tagsForCreate) : '';
  const timeline = await timelineRequest<Record<string, unknown>>({
    ...args,
    path: '/timelines',
    method: 'POST',
    body: {
      title: args.title,
      description: args.description,
      tags: [encodedTags],
    },
  });
  return { timeline };
}

export type TimelineByIdArgs = TimelineSessionArgs & {
  timelineId: string;
  signal?: AbortSignal;
};

export async function deleteTimeline(args: TimelineByIdArgs): Promise<void> {
  await timelineRequest({
    ...args,
    path: `/timelines/${args.timelineId}`,
    method: 'DELETE',
  });
}

export type ReorderTimelineArgs = TimelineByIdArgs & {
  order: string[];
};

export async function reorderTimelineEvents(args: ReorderTimelineArgs): Promise<void> {
  await timelineRequest({
    ...args,
    path: `/timelines/${args.timelineId}/events/display-order`,
    method: 'PUT',
    body: args.order,
  });
}

export type PatchTimelineArgs = TimelineByIdArgs & {
  patch: {
    tags?: string[] | string;
    title?: string;
    description?: string;
    courtroomTitle?: string;
    courtroomDescription?: string;
    media?: string;
  };
};

export async function patchTimeline(args: PatchTimelineArgs): Promise<void> {
  const rawTags = args.patch.tags;
  const tags = !rawTags
    ? ['']
    : Array.isArray(rawTags)
      ? rawTags.map((tag) => safeDecodeURIComponent(String(tag)))
      : [safeDecodeURIComponent(String(rawTags))];
  await timelineRequest({
    ...args,
    path: `/timelines/${args.timelineId}`,
    method: 'PATCH',
    body: {
      title: args.patch.title || '',
      description: args.patch.description || '',
      courtroomTitle: args.patch.courtroomTitle || '',
      courtroomDescription: args.patch.courtroomDescription || '',
      media: args.patch.media || '',
      tags,
    },
  });
}

export type PptxJobResponse = {
  id?: string;
  status?: string;
  errorMessage?: string;
  s3OutputUrl?: string;
  timelineId?: string;
  timelineUpdatedAt?: string;
  createdAt?: string;
  createdBy?: string;
  completedAt?: string;
};

export type PptxJobListResponse = {
  jobs: PptxJobResponse[];
  total: number;
};

export async function createTimelinePptxJob(args: TimelineByIdArgs): Promise<PptxJobResponse> {
  return timelineRequest<PptxJobResponse>({
    ...args,
    path: `/timelines/${args.timelineId}/pptx-jobs`,
    method: 'POST',
  });
}

export async function getTimelinePptxJobStatus(args: TimelineByIdArgs & { jobId: string }): Promise<PptxJobResponse> {
  return timelineRequest<PptxJobResponse>({
    ...args,
    path: `/timelines/${args.timelineId}/pptx-jobs/${args.jobId}`,
    method: 'GET',
  });
}

export async function listTimelinePptxJobs(args: TimelineByIdArgs): Promise<PptxJobListResponse> {
  return timelineRequest<PptxJobListResponse>({
    ...args,
    path: `/timelines/${args.timelineId}/pptx-jobs`,
    method: 'GET',
  });
}

export type PptxDownloadResponse = {
  url: string;
  filename: string;
};

/**
 * Resolve a short-lived presigned S3 URL for a completed PPTX job. The caller
 * points the browser at this URL to download the bundle directly from S3,
 * bypassing the cell API edge's response-size limit (large bundles otherwise
 * fail with a 413).
 */
export async function downloadTimelinePptxArtifact(
  args: TimelineByIdArgs & { jobId: string; filename?: string; signal?: AbortSignal },
): Promise<PptxDownloadResponse> {
  return timelineRequest<PptxDownloadResponse>({
    ...args,
    path: `/timelines/${args.timelineId}/pptx-jobs/${args.jobId}/download`,
    method: 'GET',
    query: args.filename ? { filename: args.filename } : undefined,
  });
}

type SlideLike = Record<string, unknown>;

export type TimelineEventWriteBaseArgs = TimelineSessionArgs & {
  docId: string;
  signal?: AbortSignal;
};

async function timelineEventWrite(
  args: TimelineSessionArgs & {
    path: string;
    method: 'POST' | 'PUT' | 'DELETE';
    body?: unknown;
    signal?: AbortSignal;
    timeoutMs?: number;
  },
): Promise<{ status: number; json: unknown | null }> {
  const { path, method, body, signal, timeoutMs, cellId, idToken } = args;
  return withTokenAuthRecovery(args.accessToken, async (accessToken) => {
    const res = await timelineServiceFetch({
      cellId,
      accessToken,
      idToken,
      path,
      method,
      body,
      signal,
      timeoutMs,
    });

    let json: unknown | null = null;
    const ct = res.headers.get('content-type');
    if (ct?.includes('application/json')) {
      json = await res.json().catch(() => null);
    }

    if (!res.ok) {
      const detail = json === null || json === undefined ? '' : JSON.stringify(json);
      throw new TimelineApiError(`timeline-service ${path} returned ${res.status}`, res.status, detail || null);
    }

    return { status: res.status, json };
  });
}

export type CreateTimelineEventArgs = TimelineEventWriteBaseArgs & { event: SlideLike };

/** Same contract as legacy BFF: `{ response: upstreamHttpStatus }` (e.g. 201 for create). */
export async function createTimelineEvent(args: CreateTimelineEventArgs): Promise<{ response: number }> {
  const payload = mapSlideToTimelineEventPayload(args.event);
  const encodedDocId = encodeURIComponent(args.docId);
  const { status } = await timelineEventWrite({
    ...args,
    path: `/timelines/${encodedDocId}/events`,
    method: 'POST',
    body: payload,
    timeoutMs: 90_000,
  });
  return { response: status };
}

export type UpdateTimelineEventArgs = TimelineEventWriteBaseArgs & { event: SlideLike };

export async function updateTimelineEvent(
  args: UpdateTimelineEventArgs,
): Promise<{ response: number; event?: unknown }> {
  const id = args.event.id;
  if (id === undefined || id === null || String(id).trim() === '') {
    throw new TimelineApiError('Event id is required for update.');
  }
  const payload = mapSlideToTimelineEventPayload(args.event);
  const encodedDocId = encodeURIComponent(args.docId);
  const encodedEventId = encodeURIComponent(String(id));
  const { status, json } = await timelineEventWrite({
    ...args,
    path: `/timelines/${encodedDocId}/events/${encodedEventId}`,
    method: 'PUT',
    body: payload,
  });
  const bodyObj = json && typeof json === 'object' ? (json as { event?: unknown }) : null;
  return { response: status, event: bodyObj?.event };
}

export type DeleteTimelineEventArgs = TimelineEventWriteBaseArgs & { eventId: string };

export async function deleteTimelineEvent(args: DeleteTimelineEventArgs): Promise<{ response: number }> {
  const encodedDocId = encodeURIComponent(args.docId);
  const encodedEventId = encodeURIComponent(args.eventId);
  const { status } = await timelineEventWrite({
    ...args,
    path: `/timelines/${encodedDocId}/events/${encodedEventId}`,
    method: 'DELETE',
    timeoutMs: 90_000,
  });
  return { response: status };
}

export const timelineKeys = {
  all: ['timeline'] as const,
  timelines: (cellId: string | null, tags: string | null) =>
    [...timelineKeys.all, 'timelines', cellId, tags] as const,
  pptxJob: (cellId: string | null, timelineId: string | null, jobId: string | null) =>
    [...timelineKeys.all, 'pptx', cellId, timelineId, jobId] as const,
};
